import React, { ForwardedRef } from "react";
import clsx from "clsx";
import MuiButton from "@mui/material/Button";

import Tooltip from "../Tooltip";
import { ButtonProps } from "./Button.types";
import { useButtonStyles, useStyles } from "./Button.styles";
import { CircularProgress } from "@mui/material";

function Button(props: ButtonProps, ref: ForwardedRef<HTMLButtonElement>) {
  const {
    color = "primary",
    className,
    variant,
    noWrap,
    loading,
    children,
    size,
    noPadding,
    justifyContent,
    preventClickTime = 500,
    onClick,
    disabled,
    disabledTooltip,
    tooltipAction,
    ...other
  } = props;

  const buttonClasses = useButtonStyles({ noPadding, justifyContent });

  const classes = useStyles({ color });

  const clickTimeoutRef = React.useRef<number>();

  const hasClickedRef = React.useRef(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      return;
    }
    if (preventClickTime) {
      if (!hasClickedRef.current) {
        onClick?.(event);
      }
      hasClickedRef.current = true;
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = window.setTimeout(() => {
        hasClickedRef.current = false;
      }, preventClickTime);
    } else {
      onClick?.(event);
    }
  };

  const rootClassName = clsx(className, {
    [classes.contained]: variant === "contained",
    [classes.outlined]: variant === "outlined",
    [classes.text]: variant === "text",
    [classes.noWrap]: noWrap,
  });

  const button = (
    <MuiButton
      {...other}
      className={rootClassName}
      classes={buttonClasses}
      disableElevation
      disableFocusRipple
      disableRipple
      disableTouchRipple
      ref={ref}
      size={size}
      variant={variant}
      onClick={handleClick}
      aria-disabled={!!disabled}
    >
      {loading && (
        <CircularProgress
          className={classes.loading}
          size={size === "small" ? 16 : 20}
          color="inherit"
        />
      )}
      {children}
    </MuiButton>
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip} action={tooltipAction}>
        {button}
      </Tooltip>
    );
  }

  return button;
}

export default React.forwardRef(Button);
